import {
  Bookmark,
  Check,
  Pencil,
  Plus,
  Save,
  Trash2,
  type LucideIcon,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { CONTROLS } from '@brickup/shared/locales/de/controls'
import {
  Button,
  cn,
  MENU_ITEM_CLASS,
  MENU_SEPARATOR_CLASS,
  Popover,
} from '@/components/base'
import { EmptyState } from '@/components/EmptyState'
import { GroupLabel } from '@/components/GroupLabel'
import {
  FILTER_CHIP_ACTIVE,
  FILTER_CHIP_CLASS,
} from '@/components/Internal/FilterChip'

/** One saved filter, as the popover lists it. */
export type ToolbarView = { id: number; name: string }

export type ToolbarViewsProps = {
  /** The bar's own reset, which the "all" row is: one action, so the row and
   *  the reset button cannot come to mean different things. */
  onReset: () => void
  /** What this screen calls a saved filter — the popover heads itself with it,
   *  because the noun is the screen's (a Sammlung, an Ansicht), not the bar's. */
  label: string
  views: ToolbarView[]
  activeId: number | null
  /** True once the filter no longer matches the open view. */
  dirty: boolean
  onApply: (id: number) => void
  /** Opens the screen's own naming dialog. */
  onSaveNew: () => void
  onUpdate: () => void
  onRename: (view: ToolbarView) => void
  onDelete: (view: ToolbarView) => void
}

/**
 * The saved filters of a list, behind a bookmark.
 *
 * A saved filter is the state of the whole bar, so it sits apart from the
 * chips — beside the count it produces. The mark fills and takes the view's
 * name once one is open, and a dot appears the moment the filter stops
 * matching it: a bar showing a name without that is a claim nothing supports.
 */
export function ToolbarViews({
  label,
  views,
  activeId,
  dirty,
  onApply,
  onReset,
  onSaveNew,
  onUpdate,
  onRename,
  onDelete,
}: ToolbarViewsProps) {
  const [open, setOpen] = useState(false)
  const active = views.find(view => view.id === activeId) ?? null

  const act = (run: () => void) => () => {
    setOpen(false)
    run()
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      className="w-72 p-1.5"
      trigger={
        <button
          type="button"
          title={active ? active.name : CONTROLS.views.save}
          className={cn(
            FILTER_CHIP_CLASS,
            active && FILTER_CHIP_ACTIVE,
            'max-w-48',
          )}
        >
          <Bookmark
            size={13}
            className="shrink-0"
            fill={active ? 'currentColor' : 'none'}
            aria-hidden
          />
          {active ? <span className="truncate">{active.name}</span> : null}
          {dirty ? (
            // The soft tone on the filled chip: `warn` against the accent it
            // sits on is 1.8:1, which is a dot nobody sees.
            <span
              aria-label={CONTROLS.views.unsaved}
              className={cn(
                'h-1.5 w-1.5 shrink-0 rounded-full',
                active ? 'bg-warn-soft' : 'bg-warn',
              )}
            />
          ) : null}
        </button>
      }
    >
      <GroupLabel className="px-2 py-1.5">{label}</GroupLabel>

      <ul className="flex flex-col">
        {/* The one entry that is not a saved filter. */}
        <ViewRow
          checked={!active}
          label={CONTROLS.filter.all}
          onSelect={act(onReset)}
        />

        {views.map(view => (
          <ViewRow
            key={view.id}
            checked={view.id === activeId}
            label={view.name}
            onSelect={act(() => onApply(view.id))}
            actions={
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  icon={Pencil}
                  tooltip={COMMON.rename}
                  onClick={act(() => onRename(view))}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  icon={Trash2}
                  tooltip={COMMON.delete}
                  onClick={act(() => onDelete(view))}
                />
              </>
            }
          />
        ))}
      </ul>

      {views.length === 0 ? (
        <EmptyState
          layout="inline"
          title={CONTROLS.views.none}
          className="px-2 pb-1.5"
        />
      ) : null}

      <div className={MENU_SEPARATOR_CLASS} />

      <div className="flex flex-col">
        {dirty && active ? (
          <ViewAction
            icon={Save}
            label={CONTROLS.views.update}
            onClick={act(onUpdate)}
          />
        ) : null}
        <ViewAction
          icon={Plus}
          label={
            dirty && active ? CONTROLS.views.saveAsNew : CONTROLS.views.save
          }
          onClick={act(onSaveNew)}
        />
      </div>
    </Popover>
  )
}

/** One line in the popover's foot — what the reader can do with this filter. */
function ViewAction({
  icon: Icon,
  label,
  onClick,
}: {
  icon: LucideIcon
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        MENU_ITEM_CLASS,
        'text-[12.5px] font-medium text-primary hover:bg-line-soft',
      )}
    >
      <Icon size={13} className="shrink-0" aria-hidden />
      {label}
    </button>
  )
}

/** One entry of the list: a check slot, a name, and what may be done to it. */
function ViewRow({
  checked,
  label,
  onSelect,
  actions,
}: {
  checked: boolean
  label: string
  onSelect: () => void
  /** Shown on hover and on keyboard focus, so a view is renamed or dropped
   *  without a second surface for the same list. */
  actions?: ReactNode
}) {
  return (
    <li className="group flex flex-row items-center gap-1 rounded-md pr-1 hover:bg-line-soft">
      <button
        type="button"
        onClick={onSelect}
        className={cn(MENU_ITEM_CLASS, 'min-w-0 flex-1 text-[12.5px]')}
      >
        <span className="w-3 shrink-0 text-primary">
          {checked ? <Check size={12} /> : null}
        </span>
        <span className="truncate">{label}</span>
      </button>
      {actions ? (
        <span className="flex shrink-0 flex-row opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
          {actions}
        </span>
      ) : null}
    </li>
  )
}
